import useApi from '../hooks/useApi';
import { getPremiumMigrationInfo } from './premiumMigrationInfoCache';
import { hasPremiumMigrationData } from './premiumMigrationEligibility';
import { getFiveYearCutoverDetails } from './fiveYearCutoverCache';
import { hasValidFiveYearCutoverEndDate } from './fiveYearCutover';
import { isFiveYearMigratedCustomer, isFiveYearMigrationPopupEligible } from './fiveYearMigrationEligibility';

export const PREMIUM_MIGRATION_DIALOG_SEEN_KEY = 'premiumMigrationDialogSeen';
export const FIVE_YEAR_MIGRATION_DIALOG_SEEN_KEY = 'fiveYearMigrationDialogSeen';

const idleFiveYearDetails = Object.freeze({
  status: 0,
  cutOverDt: null,
  fullEndDt: null,
  originalPlanQty: null,
  scheduledQty: null
});

export function getMigrationDialogSeenKey(baseKey, userId) {
  return userId ? `${baseKey}_${userId}` : null;
}

export function hasSeenMigrationDialog(seenKey) {
  if (!seenKey) {
    return true;
  }

  return localStorage.getItem(seenKey) === '1';
}

/**
 * Evaluates Premium and Five-Year migration popup/tour-deferral state.
 * Both APIs are fetched in parallel. Five-Year migrated users (status 1 or -1)
 * must not see the Premium popup even when /migrationInfo returns data.
 */
export async function evaluateMigrationDialogState(options = {}) {
  const { skipFiveYearCheck = false } = options;

  const fiveYearPromise = skipFiveYearCheck
    ? Promise.resolve(idleFiveYearDetails)
    : getFiveYearCutoverDetails().catch(() => idleFiveYearDetails);

  const [muiStatusRes, migrationPayload, fiveYearDetails] = await Promise.all([
    useApi('MUI_STATUS', {}, {}, ['_Id']),
    getPremiumMigrationInfo(),
    fiveYearPromise
  ]);

  const tourCompleted = muiStatusRes?.details?.tourCompleted === true;
  const premiumPayload = migrationPayload || {};
  const hasPremiumMigration = hasPremiumMigrationData(premiumPayload);
  const isFiveYearMigrated = isFiveYearMigratedCustomer(fiveYearDetails.status);
  const fiveYearPopupEligible = isFiveYearMigrationPopupEligible(fiveYearDetails.status);
  const hasFiveYearWizardDate = hasValidFiveYearCutoverEndDate(fiveYearDetails.fullEndDt);

  const showPremiumDialog = hasPremiumMigration && !isFiveYearMigrated && !tourCompleted;
  const showFiveYearDialog = fiveYearPopupEligible && hasFiveYearWizardDate && !tourCompleted;
  const shouldDeferTourForMigration = showPremiumDialog || showFiveYearDialog;

  return {
    tourCompleted,
    hasPremiumMigration,
    premiumPayload,
    fiveYearDetails,
    isFiveYearMigrated,
    fiveYearPopupEligible,
    showPremiumDialog,
    showFiveYearDialog,
    shouldDeferTourForMigration,
    markPremiumSeenBecauseTourCompleted: hasPremiumMigration && !isFiveYearMigrated && tourCompleted,
    markFiveYearSeenBecauseTourCompleted: fiveYearPopupEligible && tourCompleted
  };
}
